import {
  DitherAlgorithm,
  ProcessedImageData,
  AlgorithmOptions,
  InputImageElement,
  ImageProcessorConfig,
  ImageProcessorAutoConfig,
} from './esc-pos-image.types';
import { ImageLoader } from './image-loader';
import { ImageUtils } from './image-utils';
import { flatten } from './flatten';
import { threshold } from './threshold';
import { floydSteinberg } from './floyd-steinberg';
import { atkinson } from './atkinson';
import { bayer } from './bayer';

/**
 * Main image processor class for converting images to ESC/POS compatible format
 */
export class ImageProcessor {
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;

  constructor() {
    this.canvas = document.createElement('canvas');
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not create 2D canvas context');
    }
    this.context = ctx;
  }

  /**
   * Convert input element to ImageData
   */
  private getImageData(element: InputImageElement, width?: number, height?: number): ImageData {
    if (element instanceof ImageData) {
      return element;
    }

    // Validate that the image is fully loaded and has valid dimensions
    ImageLoader.validateImageDimensions(element);

    let sourceWidth: number;
    let sourceHeight: number;

    if (element instanceof HTMLImageElement) {
      sourceWidth = element.naturalWidth || element.width;
      sourceHeight = element.naturalHeight || element.height;
    } else if (element instanceof HTMLCanvasElement) {
      sourceWidth = element.width;
      sourceHeight = element.height;
    } else {
      throw new Error('Unsupported image element type');
    }

    // Use provided dimensions or original dimensions
    const targetWidth = width || sourceWidth;
    const targetHeight = height || sourceHeight;

    // Setup canvas with target dimensions
    this.canvas.width = targetWidth;
    this.canvas.height = targetHeight;

    // Clear canvas and draw the image
    this.context.clearRect(0, 0, targetWidth, targetHeight);
    this.context.drawImage(element, 0, 0, targetWidth, targetHeight);

    // Get image data
    return this.context.getImageData(0, 0, targetWidth, targetHeight);
  }

  /**
   * Process an image with the specified algorithm
   */
  process(config: ImageProcessorConfig, element: InputImageElement): ProcessedImageData {
    const { width, height, algorithm, options } = config;

    // Convert input to ImageData
    const imageData = this.getImageData(element, width, height);

    // Flatten the image to remove alpha channel with white background
    const flattenedImage = flatten(imageData, [255, 255, 255]);

    // Apply the selected dithering algorithm
    return this.applyAlgorithm(flattenedImage, algorithm, options);
  }

  /**
   * Process an image with the specified algorithm (async version)
   * This method ensures images are fully loaded before processing
   */
  async processAsync(
    config: ImageProcessorConfig,
    element: InputImageElement,
  ): Promise<ProcessedImageData> {
    // Ensure the image is fully loaded
    const loadedElement = await ImageLoader.ensureLoaded(element);

    // Process the loaded image
    return this.process(config, loadedElement);
  }

  /**
   * Process an image with automatic scaling based on maxWidth
   * This is the recommended method for most use cases
   */
  processAuto(config: ImageProcessorAutoConfig, element: InputImageElement): ProcessedImageData {
    const { maxWidth, maxHeight, algorithm, options } = config;

    // Calculate optimal dimensions maintaining aspect ratio
    const scaledDimensions = ImageUtils.scaleToMaxWidth(element, maxWidth, maxHeight);

    // Validate dimensions for ESC/POS compatibility
    ImageUtils.validateEscPosDimensions(scaledDimensions.width, scaledDimensions.height);

    // Process with calculated dimensions
    return this.process(
      {
        width: scaledDimensions.width,
        height: scaledDimensions.height,
        algorithm,
        options,
      },
      element,
    );
  }

  /**
   * Process an image with automatic scaling (async version)
   * This method ensures images are fully loaded before processing
   */
  async processAutoAsync(
    config: ImageProcessorAutoConfig,
    element: InputImageElement,
  ): Promise<ProcessedImageData> {
    // Ensure the image is fully loaded
    const loadedElement = await ImageLoader.ensureLoaded(element);

    // Process the loaded image with auto scaling
    return this.processAuto(config, loadedElement);
  }

  /**
   * Apply a specific dithering algorithm
   */
  private applyAlgorithm(
    imageData: ProcessedImageData,
    algorithm: DitherAlgorithm,
    options: AlgorithmOptions = {},
  ): ProcessedImageData {
    switch (algorithm) {
      case 'threshold':
        return threshold(imageData, { threshold: options.threshold || 128 });

      case 'floydsteinberg':
        return floydSteinberg(imageData, { threshold: options.threshold });

      case 'atkinson':
        return atkinson(imageData, { threshold: options.threshold });

      case 'bayer':
        return bayer(imageData, { threshold: options.threshold });

      default:
        throw new Error(`Unknown dithering algorithm: ${algorithm}`);
    }
  }

  /**
   * Get pixel value (0 or 1) at specific coordinates from processed image
   * Used for ESC/POS binary data generation
   */
  getPixel(processedImageData: ProcessedImageData, x: number, y: number): number {
    const { data, width, height } = processedImageData;

    if (x >= width || y >= height || x < 0 || y < 0) {
      return 0; // Return 0 for out of bounds pixels
    }

    const idx = (y * width + x) * 4;
    const red = data[idx];

    // Return 1 for black pixels, 0 for white pixels
    return red > 0 ? 0 : 1;
  }

  /**
   * Convert processed image data to binary data for ESC/POS printing
   * Returns data organized for column-based printing (ESC *)
   * ESC/POS column format: each column is processed in 24-pixel high strips
   */
  getColumnData(processedImageData: ProcessedImageData): Uint8Array[] {
    const { width, height } = processedImageData;
    const data: Uint8Array[] = [];

    // Process in 24-pixel high strips (3 bytes per column)
    for (let strip = 0; strip < Math.ceil(height / 24); strip++) {
      const bytes = new Uint8Array(width * 3);

      for (let x = 0; x < width; x++) {
        // Each column is 3 bytes (24 pixels vertically)
        // Byte 0: pixels 0-7, Byte 1: pixels 8-15, Byte 2: pixels 16-23
        for (let byteIdx = 0; byteIdx < 3; byteIdx++) {
          let byteValue = 0;

          // Pack 8 vertical pixels into each byte (MSB first)
          for (let bit = 0; bit < 8; bit++) {
            const y = strip * 24 + byteIdx * 8 + bit;
            const pixel = this.getPixel(processedImageData, x, y);
            // Set bit from MSB (bit 7) to LSB (bit 0)
            byteValue |= pixel << (7 - bit);
          }

          bytes[x * 3 + byteIdx] = byteValue;
        }
      }

      data.push(bytes);
    }

    return data;
  }

  /**
   * Convert processed image data to binary data for ESC/POS printing
   * Returns data organized for raster printing (GS v)
   */
  getRasterData(processedImageData: ProcessedImageData): Uint8Array {
    const { width, height } = processedImageData;
    const bytes = new Uint8Array((width * height) >> 3);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x += 8) {
        let byteValue = 0;

        // Pack 8 horizontal pixels into each byte
        for (let bit = 0; bit < 8; bit++) {
          const pixel = this.getPixel(processedImageData, x + bit, y);
          byteValue |= pixel << (7 - bit);
        }

        bytes[y * (width >> 3) + (x >> 3)] = byteValue;
      }
    }

    return bytes;
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    // Canvas will be garbage collected when this instance is disposed
  }
}
